<script setup lang="ts">
import { computed } from 'vue'
import { usePostHeight } from '@greenbow/composables'
import { useStatuspage } from '@/composables/useStatuspage'
import Accordion from '@/components/Accordion.vue'
import ErrorState from '@/components/ErrorState.vue'
import SkeletonBlock from '@/components/SkeletonBlock.vue'

// ?compact=1 — renders only the status banner. Useful as an inline
// status badge embedded in a sidebar/header (selvbetjening, app,
// partner sites).
const isCompact = new URLSearchParams(window.location.search).get('compact') === '1'

const {
  status,
  componentGroups,
  incidents,
  scheduledMaintenances,
  lastUpdated,
  isLoading,
  error,
  refetch,
} = useStatuspage()

usePostHeight()

const overallStatusText = computed(() => {
  switch (status.value?.indicator) {
    case 'none':
      return 'Alle systemer kører normalt'
    case 'minor':
      return 'Mindre driftsforstyrrelser'
    case 'major':
      return 'Større driftsforstyrrelser'
    case 'critical':
      return 'Kritiske driftsforstyrrelser'
    default:
      return 'Alle systemer kører normalt'
  }
})

const STATUS_LABELS: Record<string, string> = {
  operational: 'I drift',
  degraded_performance: 'Nedsat ydelse',
  partial_outage: 'Delvist nede',
  major_outage: 'Nede',
  under_maintenance: 'Vedligehold',
}

function statusLabel(s: string) {
  return STATUS_LABELS[s] ?? s
}

const dateFormatter = new Intl.DateTimeFormat('da-DK', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

function formatTime(dateStr: string) {
  return dateFormatter.format(new Date(dateStr))
}
</script>

<template>
  <main class="driftstatus" :class="{ 'driftstatus--compact': isCompact }">
    <h1 v-if="!isCompact" class="driftstatus__heading">
      Drift<span class="driftstatus__heading-accent">status</span>
    </h1>

    <div v-if="isLoading" class="driftstatus__loading">
      <SkeletonBlock :height="isCompact ? '32px' : '80px'" radius="12px" />
      <SkeletonBlock v-if="!isCompact" height="300px" radius="12px" />
    </div>

    <ErrorState
      v-else-if="error"
      message="Driftsstatus kunne ikke hentes. Prøv igen senere."
      @retry="refetch()"
    />

    <template v-else>
      <div
        class="driftstatus__banner"
        :class="`driftstatus__banner--${status?.indicator ?? 'none'}`"
      >
        <span class="driftstatus__banner-dot" />
        <span class="driftstatus__banner-text">{{ overallStatusText }}</span>
      </div>

      <template v-if="!isCompact">
        <h2 class="driftstatus__section-title">Systemer</h2>
        <div class="driftstatus__list">
          <div
            v-for="component in componentGroups.ungrouped"
            :key="component.id"
            class="driftstatus__row"
          >
            <span class="driftstatus__row-name">{{ component.name }}</span>
            <div class="driftstatus__row-status">
              <span class="driftstatus__badge" :class="`driftstatus__badge--${component.status}`">
                {{ statusLabel(component.status) }}
              </span>
              <span class="driftstatus__dot" :class="`driftstatus__dot--${component.status}`" />
            </div>
          </div>

          <Accordion
            v-for="{ group, children, worst } in componentGroups.groups"
            :key="group.id"
            class="driftstatus__group"
          >
            <template #summary>
              <span class="driftstatus__row-name driftstatus__row-name--group">
                {{ group.name }}
                <span class="driftstatus__count">({{ children.length }})</span>
              </span>
              <div class="driftstatus__row-status">
                <span class="driftstatus__badge" :class="`driftstatus__badge--${worst}`">
                  {{ statusLabel(worst) }}
                </span>
                <span class="driftstatus__dot" :class="`driftstatus__dot--${worst}`" />
              </div>
            </template>
            <div
              v-for="child in children"
              :key="child.id"
              class="driftstatus__row driftstatus__row--child"
            >
              <span class="driftstatus__row-name">{{ child.name }}</span>
              <div class="driftstatus__row-status">
                <span class="driftstatus__badge" :class="`driftstatus__badge--${child.status}`">
                  {{ statusLabel(child.status) }}
                </span>
                <span class="driftstatus__dot" :class="`driftstatus__dot--${child.status}`" />
              </div>
            </div>
          </Accordion>
        </div>

        <template v-if="incidents.length">
          <h2 class="driftstatus__section-title">Aktive hændelser</h2>
          <div class="driftstatus__incidents">
            <div v-for="incident in incidents" :key="incident.id" class="driftstatus__incident">
              <div class="driftstatus__incident-head">
                <span
                  class="driftstatus__badge"
                  :class="`driftstatus__badge--impact-${incident.impact}`"
                >
                  {{ incident.impact }}
                </span>
                <span class="driftstatus__incident-name">{{ incident.name }}</span>
              </div>
              <div class="driftstatus__timeline">
                <div
                  v-for="update in incident.incident_updates"
                  :key="update.id"
                  class="driftstatus__timeline-item"
                >
                  <div class="driftstatus__timeline-dot" />
                  <div class="driftstatus__timeline-content">
                    <div class="driftstatus__timeline-meta">
                      <span class="driftstatus__timeline-status">{{ update.status }}</span>
                      <span class="driftstatus__timeline-time">
                        {{ formatTime(update.created_at) }}
                      </span>
                    </div>
                    <p class="driftstatus__timeline-body">{{ update.body }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="scheduledMaintenances.length">
          <h2 class="driftstatus__section-title">Planlagt vedligehold</h2>
          <div class="driftstatus__incidents">
            <div v-for="m in scheduledMaintenances" :key="m.id" class="driftstatus__incident">
              <div class="driftstatus__incident-head">
                <span class="driftstatus__badge driftstatus__badge--info">Vedligehold</span>
                <span class="driftstatus__incident-name">{{ m.name }}</span>
              </div>
              <div class="driftstatus__incident-schedule">
                {{ formatTime(m.scheduled_for) }} &mdash; {{ formatTime(m.scheduled_until) }}
              </div>
            </div>
          </div>
        </template>

        <template v-if="!incidents.length && !scheduledMaintenances.length">
          <div class="driftstatus__no-incidents">Ingen aktive hændelser</div>
        </template>

        <div class="driftstatus__footer">
          <span v-if="lastUpdated" class="driftstatus__footer-updated">
            Sidst opdateret: {{ formatTime(lastUpdated) }}
          </span>
          <a
            href="https://greenbow.statuspage.io"
            target="_blank"
            rel="noopener noreferrer"
            class="driftstatus__footer-link"
          >
            Se fuld statusside →
          </a>
        </div>
      </template>
    </template>
  </main>
</template>

<style lang="scss" scoped>
.driftstatus {
  display: flex;
  flex-direction: column;
  gap: space(6);
  color: color('ink');

  &--compact {
    gap: 0;

    .driftstatus__banner {
      padding: space(2) space(4);
      box-shadow: none;
    }

    .driftstatus__banner-dot {
      width: 10px;
      height: 10px;
    }

    .driftstatus__banner-text {
      font-size: 0.875rem;
    }
  }

  &__heading {
    font-size: 2rem;
    font-weight: 700;
    color: color('primary');
    margin: 0;
  }

  &__heading-accent {
    color: color('accent');
  }

  &__loading {
    display: flex;
    flex-direction: column;
    gap: space(3);
  }

  &__banner {
    display: flex;
    align-items: center;
    gap: space(3);
    padding: space(5) space(6);
    border-radius: $radius-md;
    box-shadow: $shadow-sm;

    &--none {
      background: color('greenies-100');
      color: color('greenies-500');

      .driftstatus__banner-dot {
        background: color('greenies-500');
      }
    }

    &--minor {
      background: color('orange-100');
      color: color('orange-400');

      .driftstatus__banner-dot {
        background: color('orange-400');
      }
    }

    &--major,
    &--critical {
      background: color('red-100');
      color: color('red-400');

      .driftstatus__banner-dot {
        background: color('red-400');
      }
    }
  }

  &__banner-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex-shrink: 0;
    animation: driftstatus-pulse 2s ease-in-out infinite;
  }

  &__banner-text {
    font-size: 1rem;
    font-weight: 600;
  }

  &__section-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: color('grayblue-400');
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
  }

  &__list {
    background: color('white');
    border-radius: $radius-md;
    box-shadow: $shadow-sm;
    overflow: hidden;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: space(4) space(5);
    transition: background 150ms ease;
    border-bottom: 1px solid color('white-200');

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: color('white-200');
    }

    &--child {
      padding-left: space(8);
      background: color('white-200');

      &:hover {
        background: color('white-300');
      }
    }
  }

  &__row-name {
    font-weight: 600;
    color: color('primary');
    display: inline-flex;
    align-items: center;
    gap: space(2);

    &--group {
      flex: 1;
    }
  }

  &__count {
    color: color('grayblue-400');
    font-weight: 400;
  }

  &__row-status {
    display: flex;
    align-items: center;
    gap: space(3);
  }

  &__badge {
    padding: space(1) space(3);
    border-radius: $radius-pill;
    font-size: 0.75rem;
    font-weight: 600;

    &--operational {
      background: color('greenies-100');
      color: color('greenies-500');
    }

    &--degraded_performance {
      background: color('orange-100');
      color: color('orange-400');
    }

    &--partial_outage,
    &--major_outage,
    &--impact-major,
    &--impact-critical {
      background: color('red-100');
      color: color('red-400');
    }

    &--impact-minor {
      background: color('orange-100');
      color: color('orange-400');
    }

    &--impact-none,
    &--under_maintenance,
    &--info {
      background: color('white-200');
      color: color('grayblue-500');
    }
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;

    &--operational {
      background: color('greenies-500');
      animation: driftstatus-pulse 2s ease-in-out infinite;
    }

    &--degraded_performance {
      background: color('orange-400');
      animation: driftstatus-pulse 1.5s ease-in-out infinite;
    }

    &--partial_outage,
    &--major_outage {
      background: color('red-400');
      animation: driftstatus-pulse 1s ease-in-out infinite;
    }

    &--under_maintenance {
      background: color('grayblue-400');
    }
  }

  &__group {
    border-bottom: 1px solid color('white-200');

    &:last-child {
      border-bottom: none;
    }

    :deep(.accordion__summary) {
      width: 100%;
      padding: space(4) space(5);
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        background: color('white-200');
      }
    }
  }

  &__incidents {
    display: flex;
    flex-direction: column;
    gap: space(3);
  }

  &__incident {
    background: color('white');
    border-radius: $radius-md;
    box-shadow: $shadow-sm;
    padding: space(5);
  }

  &__incident-head {
    display: flex;
    align-items: center;
    gap: space(3);
    margin-bottom: space(3);
  }

  &__incident-name {
    font-weight: 600;
    color: color('primary');
  }

  &__incident-schedule {
    color: color('grayblue-400');
    font-size: 0.875rem;
  }

  &__timeline-item {
    display: flex;
    gap: space(3);
    position: relative;
    padding-bottom: space(4);

    &:not(:last-child)::before {
      content: '';
      position: absolute;
      left: 4px;
      top: 14px;
      bottom: 0;
      width: 2px;
      background: color('white-300');
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: color('primary');
    flex-shrink: 0;
    margin-top: 4px;
  }

  &__timeline-content {
    flex: 1;
    min-width: 0;
  }

  &__timeline-meta {
    display: flex;
    align-items: center;
    gap: space(3);
    margin-bottom: space(1);
  }

  &__timeline-status {
    font-weight: 600;
    color: color('primary');
    text-transform: capitalize;
    font-size: 0.875rem;
  }

  &__timeline-time {
    color: color('grayblue-400');
    font-size: 0.75rem;
  }

  &__timeline-body {
    color: color('grayblue-400');
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
  }

  &__no-incidents {
    text-align: center;
    color: color('grayblue-400');
    padding: space(5);
    background: color('white');
    border-radius: $radius-md;
    box-shadow: $shadow-sm;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: space(3);
    padding-top: space(4);
    border-top: 1px solid color('white-200');
  }

  &__footer-updated {
    color: color('grayblue-400');
    font-size: 0.75rem;
  }

  &__footer-link {
    color: color('accent');
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      color: color('accent-dark');
    }
  }
}

@keyframes driftstatus-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.6;
    transform: scale(1.15);
  }
}

@media (prefers-reduced-motion: reduce) {
  .driftstatus__banner-dot,
  .driftstatus__dot {
    animation: none;
  }
}
</style>
